import { useEffect, useRef } from "react";

import api from "./api";

export const detectSupervisionPlatform = ({
  appPlatform = process.env.REACT_APP_PLATFORM || "web",
  androidDistribution = process.env.REACT_APP_ANDROID_DISTRIBUTION || "play",
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  navigatorPlatform = typeof navigator === "undefined" ? "" : navigator.platform,
  maxTouchPoints = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
} = {}) => {
  if (appPlatform === "windows") return "WINDOWS";
  if (appPlatform === "android") {
    return String(androidDistribution).toLowerCase() === "direct" ? "ANDROID_DIRECT" : "ANDROID_PLAY";
  }
  const ipadDesktopMode = /Mac/i.test(navigatorPlatform) && Number(maxTouchPoints) > 1;
  return /iPad|iPhone|iPod/i.test(userAgent) || ipadDesktopMode ? "IOS" : "WEB";
};

export const HEARTBEAT_INTERVAL_MS = 60_000;

export const SUPERVISION_AREAS = Object.freeze({
  LOGIN: "LOGIN",
  STUDENT_DASHBOARD: "STUDENT_DASHBOARD",
  COURSES: "COURSES",
  EXERCISES: "EXERCISES",
  QCM: "QCM",
  MESSAGING: "MESSAGING",
  NOTIFICATIONS: "NOTIFICATIONS",
  ACCOUNT: "ACCOUNT",
  ADMIN: "ADMIN",
  GENERATOR: "GENERATOR",
});

const ALLOWED_AREAS = new Set(Object.values(SUPERVISION_AREAS));
export const normalizeSupervisionArea = (area) => (
  ALLOWED_AREAS.has(area) ? area : SUPERVISION_AREAS.STUDENT_DASHBOARD
);

export const sendSupervisionHeartbeat = async ({ area, city = "", region = "", latitude, longitude, accuracy } = {}) => {
  const payload = {
    platform: detectSupervisionPlatform(),
    app_version: process.env.REACT_APP_VERSION || "0.1.0",
    current_area: normalizeSupervisionArea(area),
  };
  if (city) payload.city = city;
  if (region) payload.region = region;
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    payload.latitude = latitude;
    payload.longitude = longitude;
    if (Number.isFinite(accuracy)) payload.accuracy = accuracy;
  }

  try {
    await api.post("supervision/heartbeat/", payload, { skipAuthRetry: true });
    return true;
  } catch {
    return false;
  }
};

export const requestPreciseLocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) {
    resolve(null);
    return;
  }
  navigator.geolocation.getCurrentPosition(position => resolve({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
  }), () => resolve(null), {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 300000,
  });
});

export const useSupervisionHeartbeat = (user, currentArea) => {
  const authenticated = Boolean(user);
  const areaRef = useRef(normalizeSupervisionArea(currentArea));
  const userRef = useRef(user);
  const previousAreaRef = useRef(null);
  const inFlightRef = useRef(false);
  const activeRef = useRef(false);
  const locationRef = useRef(null);

  useEffect(() => { areaRef.current = normalizeSupervisionArea(currentArea); }, [currentArea]);
  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    if (!authenticated) {
      previousAreaRef.current = null;
      return undefined;
    }
    activeRef.current = true;
    previousAreaRef.current = areaRef.current;

    const send = async () => {
      if (!activeRef.current || inFlightRef.current || document.visibilityState === "hidden") return;
      inFlightRef.current = true;
      try {
        await sendSupervisionHeartbeat({
          area: areaRef.current,
          city: userRef.current?.ville || "",
          region: userRef.current?.region || "",
          latitude: locationRef.current?.latitude,
          longitude: locationRef.current?.longitude,
          accuracy: locationRef.current?.accuracy,
        });
      } finally {
        inFlightRef.current = false;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") send();
    };

    send();
    requestPreciseLocation().then((location) => {
      if (!activeRef.current || !location) return;
      locationRef.current = location;
      send();
    });
    const timer = window.setInterval(send, HEARTBEAT_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      activeRef.current = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated || !previousAreaRef.current || previousAreaRef.current === areaRef.current) return;
    previousAreaRef.current = areaRef.current;
    if (document.visibilityState !== "hidden" && !inFlightRef.current) {
      inFlightRef.current = true;
      sendSupervisionHeartbeat({
        area: areaRef.current,
        city: userRef.current?.ville || "",
        region: userRef.current?.region || "",
        latitude: locationRef.current?.latitude,
        longitude: locationRef.current?.longitude,
        accuracy: locationRef.current?.accuracy,
      }).finally(() => { inFlightRef.current = false; });
    }
  }, [authenticated, currentArea]);
};
